/**
 * VistoeraTASK-00523 - create / rename dialog shared by both folder entrances.
 * The caller supplies the async `onSubmit`; server errors (duplicate name,
 * foreign parent, permission) surface through the caller's own error mapping.
 */
import { useEffect, useState } from 'react'
import { Input, Modal } from 'antd'
import { useTranslation } from 'react-i18next'

export type FolderNameModalState =
  | { mode: 'create'; parentId: string | null; parentName: string | null }
  | { mode: 'rename'; folderId: string; name: string }

export interface FolderNameModalProps {
  state: FolderNameModalState | null
  onCancel: () => void
  /** Resolve to close; throw / reject to keep the dialog open. */
  onSubmit: (name: string, state: FolderNameModalState) => Promise<void>
  testIdPrefix?: string
}

export default function FolderNameModal({ state, onCancel, onSubmit, testIdPrefix = 'ift' }: FolderNameModalProps) {
  const { t } = useTranslation('imageAssets')
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!state) return
    setName(state.mode === 'rename' ? state.name : '')
    setError(null)
    setSaving(false)
  }, [state])

  const submit = async () => {
    if (!state) return
    const clean = name.trim()
    if (!clean) {
      setError(t('folders.nameRequired'))
      return
    }
    setSaving(true)
    try {
      await onSubmit(clean, state)
    } catch (err) {
      const msg = (err as { message?: string } | null)?.message
      setError(msg || t('folders.loadFailed'))
    } finally {
      setSaving(false)
    }
  }

  const title =
    state?.mode === 'rename'
      ? t('folders.renameTitle')
      : state?.parentName
        ? t('folders.createChildTitle', { parent: state.parentName })
        : t('folders.createTitle')

  return (
    <Modal
      open={!!state}
      title={title}
      onCancel={onCancel}
      onOk={() => void submit()}
      confirmLoading={saving}
      destroyOnClose
      zIndex={2100}
      maskClosable={false}
    >
      <Input
        autoFocus
        maxLength={60}
        value={name}
        status={error ? 'error' : undefined}
        placeholder={t('folders.namePlaceholder')}
        onChange={(e) => {
          setName(e.target.value)
          if (error) setError(null)
        }}
        onPressEnter={() => void submit()}
        data-testid={`${testIdPrefix}-name-input`}
      />
      {error ? (
        <div role="alert" style={{ color: 'var(--danger, #ff4d4f)', marginTop: 8, fontSize: 12 }}>
          {error}
        </div>
      ) : null}
    </Modal>
  )
}
